import { useSignUp } from '@clerk/react';
import { Button, Card, CardContent, Spinner } from '@design-system';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { WaterlilyLogo } from '@client/components/WaterlilyLogo';

type Display =
  | { kind: 'pending' }
  | { kind: 'success'; title: string; body: string }
  | { kind: 'error'; title: string; body: string };

function describe(status: string | null | undefined): Display {
  switch (status) {
    case 'verified':
    case 'verified_from_the_same_client':
      return {
        kind: 'success',
        title: 'Your account is ready',
        body: 'You can safely close this tab and return to where you signed up.',
      };
    case 'expired':
      return {
        kind: 'error',
        title: 'This sign-up link has expired',
        body: 'For your security, sign-up links only work for a short time. Please go back and request a new link.',
      };
    case 'failed':
      return {
        kind: 'error',
        title: "This sign-up link didn't work",
        body: 'The link is no longer valid. Please go back and request a new link.',
      };
    case 'client_mismatch':
      return {
        kind: 'error',
        title: 'Please use the same device',
        body: 'For your security, open this link on the same device and browser where you started signing up.',
      };
    default:
      return { kind: 'pending' };
  }
}

export function EmailLinkSignupVerification() {
  const { signUp } = useSignUp();
  const [searchParams] = useSearchParams();
  // Clerk redirects to this page with ?__clerk_status=<state> on terminal
  // outcomes (expired/failed/client_mismatch). The SDK's in-memory sign-up
  // state isn't populated in those cases, so the URL param is the source of
  // truth — fall back to the SDK only when the URL doesn't carry a status.
  const status =
    signUp?.verifications.emailLinkVerification?.status ??
    searchParams.get('__clerk_status') ??
    null;
  const display = describe(status);

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <header className="fixed left-0 right-0 top-0 z-50 flex items-center border-b bg-white px-4 py-4 md:px-6">
        <WaterlilyLogo className="h-6" />
      </header>

      <main className="flex flex-1 flex-col items-center justify-center px-4 pb-12 pt-20 md:pt-24">
        <div className="w-full max-w-md">
          <Card className="bg-background gap-0 overflow-hidden rounded-2xl border p-0 shadow-none">
            <div className="p-1">
              <div className="bg-muted rounded-xl px-6 py-10 text-center">
                <div className="mb-6 flex justify-center">
                  {display.kind === 'pending' && (
                    <div className="bg-primary/10 flex size-16 items-center justify-center rounded-full">
                      <Spinner className="text-primary size-8" />
                    </div>
                  )}
                  {display.kind === 'success' && (
                    <div className="flex size-16 items-center justify-center rounded-full bg-green-50">
                      <CheckCircle2
                        className="size-9 text-green-600"
                        strokeWidth={2}
                        aria-hidden="true"
                      />
                    </div>
                  )}
                  {display.kind === 'error' && (
                    <div className="flex size-16 items-center justify-center rounded-full bg-red-50">
                      <AlertCircle
                        className="size-9 text-red-600"
                        strokeWidth={2}
                        aria-hidden="true"
                      />
                    </div>
                  )}
                </div>

                <h1 className="text-2xl font-bold tracking-tight text-gray-950">
                  {display.kind === 'pending' ? 'Checking your sign-up link…' : display.title}
                </h1>
                <p className="text-muted-foreground mx-auto mt-3 max-w-sm text-base leading-relaxed">
                  {display.kind === 'pending'
                    ? 'This will only take a moment.'
                    : display.body}
                </p>
              </div>
            </div>

            {display.kind === 'error' && (
              <CardContent className="p-6">
                <Button asChild size="lg" className="w-full">
                  <Link to="/signup">Back to sign up</Link>
                </Button>
              </CardContent>
            )}
          </Card>
        </div>
      </main>
    </div>
  );
}
